import AccountCircle from "@mui/icons-material/AccountCircle";
import Close from "@mui/icons-material/Close";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { LinearTagChip } from "components/LinearTagChip";
import { ActivityType as PrismaActivityType } from "encamp-prisma/generated/browser";
import { getActivityTitleOnly } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityType } from "generated-graphql/graphql";
import { LinearIssueStatusMap } from "hooks/useLinearIssueStatuses";
import React, { useMemo } from "react";

// Generic pilot facility type that works for both state and local jurisdiction rows
type PilotFacilityRow = {
  facilityId: string;
  facilityName: string;
  streetAddress1?: string | null;
  city?: string | null;
  facilityState: string;
  tenantId: string;
  tenantName: string;
  isDeniedTenant: boolean;
  reportId?: string | null;
  reportOrgStatus?: string | null;
  reportEncampStatus?: string | null;
  workflowStatus?: string | null;
  workflowId?: string | null;
  workflowTags: Array<{ id: string; name: string }>;
  configId: string;
  configNotes?: string | null;
  configCreatedAt: Date | string;
  configCreatedBy: string;
  currentActivityType?: ActivityType | null;
  currentActivityDisplayName?: string | null;
  isCohort: boolean;
};

interface DetailPanelContentProps {
  pilotFacilities: PilotFacilityRow[];
  onRemove: (configId: string) => void;
  removeLoading: boolean;
  linearStatusMap: LinearIssueStatusMap;
}

export const DetailPanelContent = React.memo(function DetailPanelContent({
  pilotFacilities,
  onRemove,
  removeLoading,
  linearStatusMap,
}: DetailPanelContentProps) {
  const columns: GridColDef<PilotFacilityRow>[] = useMemo(
    () => [
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack>
            <Typography variant="body2">{row.facilityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.streetAddress1, row.city, row.facilityState].filter(Boolean).join(", ")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "tenantName",
        headerName: "Organization",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">{row.tenantName}</Typography>
            {row.isDeniedTenant && (
              <Tooltip title="Tenant is on deny list, but this facility is explicitly allowed via ALLOW_FACILITY rule">
                <Chip label="Allowed (Tenant Denied)" color="success" size="small" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "reportEncampStatus",
        headerName: "Encamp Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.reportEncampStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Chip
              label={prettyPrintEnumValue(row.reportEncampStatus, undefined, true)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.workflowStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                Not Started
              </Typography>
            );
          }
          const color =
            row.workflowStatus === "Completed"
              ? "success"
              : row.workflowStatus === "Error"
                ? "error"
                : row.workflowStatus === "InProgress"
                  ? "info"
                  : "default";
          return (
            <Chip label={prettyPrintEnumValue(row.workflowStatus)} size="small" color={color} />
          );
        },
      },
      {
        field: "currentActivityStep",
        headerName: "Activity Step",
        width: 180,
        renderCell: ({ row }) => {
          const isStaffActivity = row.currentActivityType === ActivityType.Staff;

          if (row.currentActivityDisplayName) {
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                {isStaffActivity && (
                  <AccountCircle sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                )}
                <Typography variant="body2">{row.currentActivityDisplayName}</Typography>
              </Stack>
            );
          }
          if (row.currentActivityType) {
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                {isStaffActivity && (
                  <AccountCircle sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                )}
                <Typography variant="body2">
                  {getActivityTitleOnly(
                    {
                      type: row.currentActivityType as PrismaActivityType,
                      description: null,
                    },
                    undefined,
                    undefined
                  )}
                </Typography>
              </Stack>
            );
          }
          return (
            <Typography variant="body2" color="text.secondary">
              Not Started
            </Typography>
          );
        },
      },
      {
        field: "workflowTags",
        headerName: "Workflow Tags",
        width: 200,
        flex: 1,
        renderCell: ({ row }) => {
          const tags = row.workflowTags;
          if (!tags || tags.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {tags.map((tag: { id: string; name: string }) => (
                <LinearTagChip key={tag.id} tag={tag.name} linearStatusMap={linearStatusMap} />
              ))}
            </Box>
          );
        },
      },
      {
        field: "configCreatedBy",
        headerName: "Added By",
        width: 150,
        renderCell: ({ row }) => (
          <Tooltip title={`Added: ${new Date(row.configCreatedAt).toLocaleString()}`}>
            <Typography variant="body2">{row.configCreatedBy}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params) => [
          ...(params.row.reportId
            ? [
                <GridActionsCellItem
                  key="view"
                  icon={
                    <Tooltip title="View report details">
                      <OpenInNew />
                    </Tooltip>
                  }
                  label="View Report"
                  onClick={() =>
                    window.open(`/staff/fulfillment/r/${params.row.reportId}`, "_blank")
                  }
                />,
              ]
            : []),
          <GridActionsCellItem
            key="remove"
            icon={
              <Tooltip title="Remove from pilot program">
                <Close />
              </Tooltip>
            }
            label="Remove"
            onClick={() => onRemove(params.row.configId)}
            disabled={removeLoading}
          />,
        ],
      },
    ],
    [onRemove, removeLoading, linearStatusMap]
  );

  return (
    <Box sx={{ p: 2, pl: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Pilot Facilities ({pilotFacilities.length})
      </Typography>
      <DataGrid<PilotFacilityRow>
        rows={pilotFacilities}
        columns={columns}
        getRowId={(row) => row.configId}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-cell": {
            py: 1.5,
            alignItems: "center",
          },
          "& .MuiDataGrid-row": {
            minHeight: "52px !important",
          },
          // Show actions always (not just on hover)
          "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']": {
            opacity: 1,
          },
        }}
      />
    </Box>
  );
});
